/**
 * useOnlineStatus.js — Tracks browser network connectivity.
 *
 * Uses the browser's native online/offline events (window.navigator.onLine).
 * Returns a boolean — true when the browser believes it has network access.
 *
 * Note: navigator.onLine can be true even when the backend is unreachable
 * (e.g. connected to Wi-Fi but no internet). This hook handles the common
 * case of losing connectivity entirely. Backend-specific errors are handled
 * separately by fetchWithAuth returning NETWORK_ERROR.
 *
 * @returns {boolean} isOnline
 */

import { useState, useEffect } from 'react';

export function useOnlineStatus() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const goOnline  = () => setIsOnline(true);
    const goOffline = () => setIsOnline(false);

    window.addEventListener('online',  goOnline);
    window.addEventListener('offline', goOffline);

    return () => {
      window.removeEventListener('online',  goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  return isOnline;
}
